import {
  useRef,
  useCallback,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  type NotificationsWidgetProps,
  type NotificationsThemingProps,
  NOTIF_THEMING_PROP_TO_PARAM,
} from "./types";

/**
 * Ref handle exposed by LegionNotifications for imperative control.
 */
export interface LegionNotificationsHandle {
  /** Reload the widget iframe */
  refresh: () => void;
}

/**
 * Build the iframe src URL from widget props.
 */
export function buildNotificationsWidgetUrl(
  props: NotificationsWidgetProps
): string {
  const {
    baseUrl,
    theme = "light",
    maxNotifications = 10,
    autoRefresh = true,
    emptyTitle = "You're all caught up",
    emptySubtitle = "No notifications right now",
    accentColor,
    emptyIcon = "bell",
    userId,
  } = props;

  const url = new URL("/widget/notifications", baseUrl);
  url.searchParams.set("theme", theme);
  url.searchParams.set("maxNotifications", String(maxNotifications));
  url.searchParams.set("autoRefresh", String(autoRefresh));

  if (emptyTitle) url.searchParams.set("emptyTitle", emptyTitle);
  if (emptySubtitle) url.searchParams.set("emptySubtitle", emptySubtitle);

  // Default accent color depends on theme
  const resolvedAccentColor =
    accentColor ?? (theme === "dark" ? "gray" : "blue");
  url.searchParams.set("accentColor", resolvedAccentColor);

  // Validate emptyIcon
  const validIcons: Array<NotificationsWidgetProps["emptyIcon"]> = [
    "bell",
    "gift",
    "activity",
    "trophy",
  ];
  const resolvedIcon = validIcons.includes(emptyIcon) ? emptyIcon : "bell";
  url.searchParams.set("emptyIcon", resolvedIcon!);

  if (userId) {
    url.searchParams.set("userId", userId);
  }

  // NOTE: authToken is NEVER added to the URL – it's sent via postMessage

  // Append theming CSS variable params
  const themingKeys = Object.keys(NOTIF_THEMING_PROP_TO_PARAM) as Array<
    keyof NotificationsThemingProps
  >;
  for (const key of themingKeys) {
    const value = props[key];
    if (value !== undefined && value !== null && String(value).length > 0) {
      url.searchParams.set(NOTIF_THEMING_PROP_TO_PARAM[key], String(value));
    }
  }

  return url.toString();
}

/**
 * LegionNotifications – a React component that embeds the Legion
 * Notifications widget (Supabase-backed) via an iframe.
 *
 * @example
 * ```tsx
 * import { LegionNotifications } from '@legionhandtech/lht-react-widgets';
 *
 * function App() {
 *   return (
 *     <LegionNotifications
 *       baseUrl="https://your-legion-domain.com"
 *       authToken="your-jwt-token"
 *       theme="light"
 *     />
 *   );
 * }
 * ```
 */
export const LegionNotifications = forwardRef<
  LegionNotificationsHandle,
  NotificationsWidgetProps
>(function LegionNotifications(props, ref) {
  const {
    theme = "light",
    authToken,
    userId,
    maxNotifications,
    autoRefresh,
    emptyTitle,
    emptySubtitle,
    accentColor,
    emptyIcon,
    width = "100%",
    height = "400px",
    className,
    style,
    onLoad,
    onError,
    baseUrl,
    ...rest
  } = props;

  const iframeRef = useRef<HTMLIFrameElement>(null);

  useImperativeHandle(
    ref,
    () => ({
      refresh: () => {
        const iframe = iframeRef.current;
        if (iframe?.contentWindow) {
          iframe.contentWindow.location.reload();
        }
      },
    }),
    []
  );

  const src = useMemo(
    () => buildNotificationsWidgetUrl(props),
    [
      baseUrl,
      theme,
      userId,
      maxNotifications,
      autoRefresh,
      emptyTitle,
      emptySubtitle,
      accentColor,
      emptyIcon,
      ...Object.keys(NOTIF_THEMING_PROP_TO_PARAM).map(
        (k) => rest[k as keyof typeof rest]
      ),
    ]
  );

  // Send auth token via postMessage after iframe loads
  const handleLoad = useCallback(() => {
    if (authToken && iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(
        { type: "AUTH_TOKEN", token: authToken },
        baseUrl
      );
    }
    onLoad?.({ widget: "notifications", theme });
  }, [authToken, baseUrl, theme, onLoad]);

  const handleError = useCallback(() => {
    onError?.("Failed to load notifications widget");
  }, [onError]);

  const containerStyle: React.CSSProperties = {
    display: "block",
    width,
    backgroundColor: "transparent",
    ...style,
  };

  const iframeStyle: React.CSSProperties = {
    border: "none",
    borderRadius: "8px",
    overflow: "hidden",
    width: "100%",
    height,
  };

  return (
    <div className={className} style={containerStyle}>
      <iframe
        ref={iframeRef}
        src={src}
        title="Notifications Widget"
        width="100%"
        loading="lazy"
        style={iframeStyle}
        onLoad={handleLoad}
        onError={handleError}
      />
    </div>
  );
});

